import { Box, Grid } from '@chakra-ui/react';
import { useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import HourTemplate from './components/hours-template';
import { WeekCalendarDay } from './components/week-calendar-day/week-calendar-day.view';
import { WeekCalendarHeader } from './components/week-calendar-header/week-calendar-header';
import { ICalendarEventMinimumData } from './types/ICalendarEvent';
import { IEventMetadata } from './types/IEventMetadata';
import { ITimeZone } from './types/ITimeZone';
import { useWeekCalendar } from './week-calendar.controller';

export interface IInnerEventProps<TEvent> {
	event: TEvent;
	metadata: IEventMetadata<TEvent>;
}

interface IWeekCalendarProps<TEvent extends ICalendarEventMinimumData> {
	/**
	 * Highlight some area
	 */
	highlight: null | { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime };
	/**
	 *
	 */
	isLoading?: boolean;
	/**
	 * Controls the size in pixels of an hour.
	 * @default 60
	 */
	hourHeightInPixels?: number;
	/**
	 * Array of calendar events
	 */
	events: Array<TEvent>;
	/**
	 * TimeZone used by default in the calendar.
	 */
	timeZone: ITimeZone;
	/**
	 * Day to show in the calendar.
	 * On mobile will focus to this day.
	 * On desktop will display the week of this day.
	 */
	from: Temporal.PlainDate;
	/**
	 * Last day to show on the calendar.
	 */
	to: Temporal.PlainDate;
	/**
	 *
	 */
	defaultActiveDay: Temporal.PlainDate;
	/**
	 * Days to display in the calendar on the desktop version.
	 * Small screens will display always one day.
	 *
	 * @default 7
	 */
	visibleDays?: number;
	/**
	 *
	 * @param start
	 * @returns
	 */
	onCalendarPointerDown?: (start: Temporal.PlainDateTime) => void;
	/**
	 *
	 * @param start
	 * @returns
	 */
	onCalendarPointerMove?: (start: Temporal.PlainDateTime) => void;
	/**
	 *
	 * @param start
	 * @returns
	 */
	onCalendarPointerUp?: (start: Temporal.PlainDateTime) => void;
	/**
	 * Callback to execute when the user changes the range that is visualizing.
	 */
	onRangeChanged?: (args: { start: Temporal.PlainDate; end: Temporal.PlainDate }) => void;
	/**
	 * Callback to execute when a calendar event is clicked.
	 */
	onEventClick: (args: { event: React.MouseEvent; calendarEvent: TEvent }) => void;
	/**
	 * Callback when the calendar header is clicked.
	 */
	onDayClick?: (value: Temporal.PlainDate) => void;
	/**
	 *
	 */
	InnerEvent?: React.ElementType<IInnerEventProps<TEvent>>;
	/**
	 * The vertical scroll offset in pixels
	 */
	verticalScroll?: number;
	/**
	 * Listen to vertical scroll on the calendar.
	 */
	onVerticalScroll?: (args: { scrollTop: number }) => void;
	/**
	 * Event triggered when the user clicks on a calendar hour.
	 */
	onCalendarHourClick?: (args: { dateTime: Temporal.ZonedDateTime }) => void;
	/**
	 * Triggered when the start / end of a calendar event is modified.
	 */
	onEventUpdated: (event: TEvent) => void;
}

export function WeekCalendar<TEvent extends ICalendarEventMinimumData>(props: IWeekCalendarProps<TEvent>) {
	const hourHeightInPixels = props.hourHeightInPixels ?? 60;

	const {
		calendarBottomRef,
		assignedEvents,
		calendarScrollRef,
		days,
		state,
		visibleDays,
		today,
		handleDayClick,
		handleScroll,
		handleWeekChange,
		handleVerticalScroll,
		handleTodayClick,
		handleEventPartialUpdated,
		handleEventUpdated,
	} = useWeekCalendar({ ...props, hourHeightInPixels });

	const [verticalScroll, setVerticalScroll] = useState(0);

	return (
		<Box
			id="calendar-wrapper"
			display="grid"
			gridTemplateRows="auto 1fr"
			w="full"
			h="full"
			overflow="hidden"
			userSelect="none"
			transition="opacity .2s"
			opacity={props.isLoading ? 0.3 : 1}
			pointerEvents={props.isLoading ? 'none' : 'initial'}
		>
			<WeekCalendarHeader
				showShadow={verticalScroll > 2}
				onTodayClick={handleTodayClick}
				today={today}
				onWeekChange={handleWeekChange}
				onDayClick={handleDayClick}
				currentDay={state.currentDay}
				timeZone={props.timeZone}
				from={props.from}
				to={props.to}
			/>

			<Grid
				as="section"
				ref={calendarBottomRef}
				id="calendar-bottom"
				gridTemplateColumns="auto 1fr"
				position="relative"
				overflow="auto"
				pt={3}
				onScroll={(e) => {
					setVerticalScroll(e.currentTarget.scrollTop);
					handleVerticalScroll(e);
				}}
				// This is to create a lower stacking context to avoid the header to be behind the
				// calendar events independently of the z-index of the calculated z-index of the event.
				zIndex={1}
			>
				<HourTemplate hourHeightPx={hourHeightInPixels} highLightTime={state.highlightTime} />

				<Grid
					as="main"
					role="rowgroup"
					ref={calendarScrollRef}
					gridTemplateColumns={{
						base: `repeat(${visibleDays}, minmax(100%, 1fr))`,
						lg: `repeat(${visibleDays}, 1fr)`,
					}}
					h="full"
					scrollSnapStop="always"
					scrollSnapType="x mandatory"
					overflowY="hidden"
					overflowX={{ base: 'auto' }}
					onScroll={handleScroll}
					borderTop="1px solid"
					borderTopColor="ui-element-divider"
				>
					{days.map((plainDate) => (
						<WeekCalendarDay
							events={assignedEvents[plainDate.toString()]}
							highlight={props.highlight}
							hourHeightInPixels={hourHeightInPixels}
							InnerEvent={props.InnerEvent}
							key={plainDate.toString()}
							onCalendarPointerDown={props.onCalendarPointerDown ?? noop}
							onCalendarPointerMove={props.onCalendarPointerMove ?? noop}
							onCalendarPointerUp={props.onCalendarPointerUp ?? noop}
							onEventClick={props.onEventClick ?? noop}
							onEventUpdated={handleEventUpdated}
							onEventPartialUpdated={handleEventPartialUpdated}
							plainDate={plainDate}
							timeZone={props.timeZone}
						/>
					))}
				</Grid>
			</Grid>
		</Box>
	);
}

function noop() {}
